import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FileText, MessageSquare, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { appealsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useTheme } from '../App';

export default function AuthorityPanel() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [appeals, setAppeals] = useState([]);
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { loadAppeals(); }, []);
  const loadAppeals = async () => { try { const res = await appealsAPI.list({ limit: 100 }); setAppeals(res.data.appeals); } catch { toast.error('Failed'); } };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !selectedAppeal) return;
    try { await appealsAPI.addNote(selectedAppeal.id, noteContent); toast.success('Note added'); setNoteContent(''); const res = await appealsAPI.get(selectedAppeal.id); setSelectedAppeal(res.data); } catch { toast.error('Failed'); }
  };
  const handleStatusChange = async (id, s) => { try { await appealsAPI.updateStatus(id, s); toast.success('Updated'); loadAppeals(); if (selectedAppeal?.id === id) { const r = await appealsAPI.get(id); setSelectedAppeal(r.data); } } catch { toast.error('Failed'); } };

  const filteredAppeals = statusFilter === 'all' ? appeals : appeals.filter(a => a.status === statusFilter);
  const inputCl = isDark ? 'bg-white/[0.03] border-white/[0.06] text-white' : 'bg-gray-50 border-gray-200 text-gray-900';
  const dropCl = isDark ? 'bg-[#0A0A0C] border-white/10' : 'bg-white border-gray-200';
  const itemCl = isDark ? 'text-white hover:bg-white/5' : 'text-gray-700 hover:bg-gray-50';
  const textP = isDark ? 'text-white' : 'text-gray-900';
  const textS = isDark ? 'text-white/60' : 'text-gray-500';
  const textM = isDark ? 'text-white/40' : 'text-gray-400';
  const card = isDark ? 'bg-[#0A0A0C]/60 backdrop-blur-xl border border-white/[0.05]' : 'bg-white border border-gray-200 shadow-sm';
  const subCard = isDark ? 'bg-white/[0.02]' : 'bg-gray-50';
  const getStatusCl = (s) => {
    const map = { submitted: 'bg-blue-500/10 text-blue-500', in_review: 'bg-purple-500/10 text-purple-500', approved: 'bg-emerald-500/10 text-emerald-500', rejected: 'bg-red-500/10 text-red-500', completed: 'bg-cyan-500/10 text-cyan-500' };
    return map[s] || (isDark ? 'bg-white/5 text-white/40' : 'bg-gray-100 text-gray-400');
  };

  return (
    <div className={`min-h-screen py-8 px-6 md:px-12 transition-colors ${isDark ? '' : 'bg-[#F8FAFC]'}`} data-testid="authority-panel">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-8">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-200'}`}>
              <FileText className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold tracking-tight ${textP}`} style={{ fontFamily: 'Outfit' }}>Court Authority Panel</h1>
              <p className={`text-sm ${textM}`}>Review and manage appeals</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={`${inputCl} rounded-lg h-9 mb-4`} data-testid="authority-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent className={dropCl}>
                  {['all','submitted','in_review','approved','rejected'].map(s => (<SelectItem key={s} value={s} className={itemCl}>{s === 'all' ? 'All' : s.replace('_',' ')}</SelectItem>))}
                </SelectContent>
              </Select>
              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
                {filteredAppeals.map(a => (
                  <motion.button key={a.id} whileHover={{ scale: 1.01 }} onClick={async () => { const r = await appealsAPI.get(a.id); setSelectedAppeal(r.data); }}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${selectedAppeal?.id === a.id ? isDark ? 'bg-white/[0.05] border-blue-500/30' : 'bg-blue-50 border-blue-300' : isDark ? 'bg-[#0A0A0C]/60 border-white/[0.05]' : 'bg-white border-gray-200'}`}
                    data-testid={`authority-appeal-${a.id}`}>
                    <div className="flex justify-between items-start">
                      <div><p className={`text-sm font-medium ${textP}`}>{a.platform}</p><p className={`text-xs mt-1 ${textM}`}>{a.issue_type}</p></div>
                      <Badge className={`text-xs ${getStatusCl(a.status)}`}>{a.status?.replace('_',' ')}</Badge>
                    </div>
                    <p className={`text-xs mt-2 ${textM}`}>{a.full_name} | {new Date(a.created_at).toLocaleDateString()}</p>
                  </motion.button>
                ))}
              </div>
            </div>
            <div className="lg:col-span-2">
              {selectedAppeal ? (
                <div className={`${card} rounded-2xl p-6 space-y-6`}>
                  <div className="flex justify-between items-start">
                    <div><h2 className={`text-xl font-semibold ${textP}`} style={{ fontFamily: 'Outfit' }}>{selectedAppeal.platform} - {selectedAppeal.issue_type}</h2><p className={`text-sm mt-1 ${textM}`}>Tracking: {selectedAppeal.tracking_code}</p></div>
                    <Select value={selectedAppeal.status} onValueChange={(v) => handleStatusChange(selectedAppeal.id, v)}>
                      <SelectTrigger className={`h-8 text-xs w-36 border rounded-full px-3 ${getStatusCl(selectedAppeal.status)}`} data-testid="authority-status-change"><SelectValue /></SelectTrigger>
                      <SelectContent className={dropCl}>{['submitted','in_review','approved','rejected','completed'].map(s => (<SelectItem key={s} value={s} className={`${itemCl} text-xs`}>{s.replace('_',' ')}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {[['Full Name', selectedAppeal.full_name], ['Email', selectedAppeal.email], ['Country', selectedAppeal.country || 'N/A'], ['Filed On', new Date(selectedAppeal.created_at).toLocaleDateString()]].map(([l,v]) => (
                      <div key={l} className={`p-3 rounded-xl ${subCard}`}><p className={`text-xs ${textM}`}>{l}</p><p className={`text-sm mt-1 ${textP}`}>{v}</p></div>
                    ))}
                  </div>
                  {selectedAppeal.custom_fields && Object.keys(selectedAppeal.custom_fields).length > 0 && (
                    <div className={`p-4 rounded-xl ${subCard}`}>
                      <p className={`text-xs mb-2 ${textM}`}>Custom Fields</p>
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(selectedAppeal.custom_fields).map(([k,v]) => (
                          <div key={k}><p className={`text-xs ${textM}`}>{k}</p><p className={`text-sm ${textP}`}>{v}</p></div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className={`p-4 rounded-xl ${subCard}`}><p className={`text-xs mb-2 ${textM}`}>Description</p><p className={`text-sm leading-relaxed ${textS}`}>{selectedAppeal.description}</p></div>
                  <div>
                    <h3 className={`text-sm font-medium mb-3 flex items-center gap-2 ${textS}`}><MessageSquare className="w-4 h-4" /> Notes</h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                      {(selectedAppeal.notes_list || []).map(n => (
                        <div key={n.id} className={`p-3 rounded-xl border ${isDark ? 'bg-white/[0.02] border-white/[0.04]' : 'bg-gray-50 border-gray-100'}`}>
                          <div className="flex justify-between items-center mb-1"><span className="text-xs text-blue-500">{n.author_email} ({n.author_role})</span><span className={`text-xs ${textM}`}>{new Date(n.created_at).toLocaleString()}</span></div>
                          <p className={`text-sm ${textS}`}>{n.content}</p>
                        </div>
                      ))}
                      {(!selectedAppeal.notes_list || selectedAppeal.notes_list.length === 0) && <p className={`text-sm text-center py-4 ${textM}`}>No notes yet</p>}
                    </div>
                    <div className="flex gap-2">
                      <Textarea value={noteContent} onChange={(e) => setNoteContent(e.target.value)} className={`${inputCl} rounded-xl resize-none min-h-[60px]`} placeholder="Add a note..." data-testid="authority-note-input" />
                      <Button onClick={handleAddNote} className="bg-blue-500 hover:bg-blue-600 rounded-xl px-4 text-white" data-testid="authority-add-note-btn"><Send className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`${card} rounded-2xl p-12 text-center`}><FileText className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-white/10' : 'text-gray-200'}`} /><p className={textM}>Select an appeal to view details</p></div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
